import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../cn';
import { Icon } from './Icon';

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'checked'
> {
  checked?: boolean | undefined;
  /** Receives the next boolean. */
  onChange?: ((checked: boolean) => void) | undefined;
  label?: ReactNode | undefined;
}

/** A real checkbox input, visually replaced. Keeps a 4 px corner so it reads differently from Radio. */
export function Checkbox({
  checked = false,
  onChange,
  label,
  disabled = false,
  className,
  id,
  ...rest
}: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <label
      htmlFor={inputId}
      className={cn(
        'inline-flex items-center gap-2',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      <input
        id={inputId}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        {...rest}
      />
      <span
        aria-hidden="true"
        className="inline-flex size-[18px] items-center justify-center rounded-xs border border-border-accented bg-surface-static-default text-content-onactioninversed peer-checked:border-transparent peer-checked:bg-surface-action-primary-default peer-focus-visible:shadow-focus-ring [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100"
      >
        <Icon name="check" size={12} strokeWidth={3} />
      </span>
      {label ? <span className="ds-body-sm text-content-default">{label}</span> : null}
    </label>
  );
}
